import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getAdminLoginFailureMessage, getUserFacingMessage } from "../../api/errors";
import { adminAuth } from "./utils/adminAuth";
import { submitTrialRequest } from "../../api/trialRequests";

type LoginMode = "super" | "account";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<LoginMode>("super");

  // Super admin (password only)
  const [superAccount, setSuperAccount] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);

  // Sub-account (tenant + account + password)
  const [subTenantId, setSubTenantId] = useState("");
  const [account, setAccount] = useState("");
  const [accountPwd, setAccountPwd] = useState("");
  const [showAccountPwd, setShowAccountPwd] = useState(false);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const [demoForm, setDemoForm] = useState({ orgName: "", contact: "", phone: "" });
  const [demoSent, setDemoSent] = useState(false);
  const [demoSubmitting, setDemoSubmitting] = useState(false);
  const [demoError, setDemoError] = useState("");

  useEffect(() => {
    if (adminAuth.isLoggedIn()) navigate("/admin", { replace: true });
  }, [navigate]);

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 600);
  };

  const handleSuperSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!superAccount.trim() || !password.trim()) return;
    setLoading(true);
    setError("");
    try {
      const result = await adminAuth.login(superAccount, password);
      if ("error" in result) {
        setError(getAdminLoginFailureMessage(result.error, "super"));
        setPassword("");
        triggerShake();
      } else {
        navigate("/admin", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAccountSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subTenantId.trim() || !account.trim() || !accountPwd.trim()) return;
    setLoading(true);
    setError("");
    try {
      const result = await adminAuth.loginWithAccount(account, accountPwd, subTenantId);
      if ("error" in result) {
        setError(getAdminLoginFailureMessage(result.error, "account"));
        setAccountPwd("");
        triggerShake();
      } else {
        navigate("/admin", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDemoSubmit = async () => {
    if (!demoForm.orgName.trim() || !demoForm.contact.trim() || demoSubmitting) return;
    setDemoError("");
    setDemoSubmitting(true);
    try {
      await submitTrialRequest({
        orgName: demoForm.orgName.trim(),
        contact: demoForm.contact.trim(),
        phone: demoForm.phone.trim() || "",
      });
      setDemoSent(true);
      setDemoForm({ orgName: "", contact: "", phone: "" });
      window.setTimeout(() => {
        setShowDemo(false);
        setDemoSent(false);
      }, 2200);
    } catch (e) {
      setDemoError(getUserFacingMessage(e, "申请提交失败，请检查网络后重试。"));
    } finally {
      setDemoSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center">
      {/* Background — 纯色渐变，不加载外链演示图 */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-950 to-red-950" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(220,38,38,0.12),_transparent_55%)]" />

      {/* Login Card */}
      <div
        className={`relative z-10 w-full max-w-md mx-4`}
        style={shake ? { animation: "shake 0.5s ease-in-out" } : {}}
      >
        {/* Logo + Title */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-red-600/90 border border-white/10 flex items-center justify-center mx-auto mb-4">
            <i className="ri-football-line text-white text-3xl" aria-hidden />
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">FanHub 协会管理</h1>
          <p className="text-gray-400 text-sm">管理后台 · 仅限授权人员访问</p>
        </div>

        {/* Card */}
        <div className="bg-white/10 backdrop-blur-xl border border-white/15 rounded-3xl p-8">
          <h2 className="text-white font-bold text-lg mb-5 flex items-center gap-2">
            <i className="ri-lock-line text-red-400"></i>
            管理员登录
          </h2>

          {/* Mode Toggle */}
          <div className="flex items-center bg-white/10 rounded-2xl p-1 mb-6">
            <button
              onClick={() => { setMode("super"); setError(""); }}
              className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                mode === "super"
                  ? "bg-red-600 text-white"
                  : "text-gray-300 hover:text-white"
              }`}
            >
              <i className="ri-shield-star-line mr-1.5"></i>超级管理员
            </button>
            <button
              onClick={() => { setMode("account"); setError(""); }}
              className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                mode === "account"
                  ? "bg-red-600 text-white"
                  : "text-gray-300 hover:text-white"
              }`}
            >
              <i className="ri-user-line mr-1.5"></i>子账号登录
            </button>
          </div>

          {/* Super Admin Form */}
          {mode === "super" && (
            <form onSubmit={handleSuperSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">租户编号</label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                    <i className="ri-building-2-line text-gray-400"></i>
                  </div>
                  <input
                    type="text"
                    value={superAccount}
                    onChange={(e) => { setSuperAccount(e.target.value); setError(""); }}
                    placeholder="请输入租户编号"
                    autoComplete="username"
                    className="w-full bg-white/10 border border-white/20 text-white placeholder-gray-500 rounded-2xl pl-11 pr-4 py-3.5 text-sm focus:outline-none focus:border-red-400 focus:bg-white/15 transition-all"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">登录密码</label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                    <i className="ri-key-2-line text-gray-400"></i>
                  </div>
                  <input
                    type={showPwd ? "text" : "password"}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(""); }}
                    placeholder="请输入超级管理员密码"
                    autoComplete="current-password"
                    className="w-full bg-white/10 border border-white/20 text-white placeholder-gray-500 rounded-2xl pl-11 pr-12 py-3.5 text-sm focus:outline-none focus:border-red-400 focus:bg-white/15 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(!showPwd)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center cursor-pointer"
                  >
                    <i className={`${showPwd ? "ri-eye-off-line" : "ri-eye-line"} text-gray-400 hover:text-gray-200 transition-colors`}></i>
                  </button>
                </div>
                {error && (
                  <div className="mt-2 flex items-center gap-2 text-red-400 text-xs">
                    <i className="ri-error-warning-line"></i>
                    {error}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || !superAccount.trim() || !password.trim()}
                className="w-full py-3.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-2xl text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                {loading ? (
                  <><i className="ri-loader-4-line animate-spin"></i>验证中...</>
                ) : (
                  <><i className="ri-login-box-line"></i>进入后台</>
                )}
              </button>
            </form>
          )}

          {/* Sub Account Form */}
          {mode === "account" && (
            <form onSubmit={handleAccountSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">租户编号</label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                    <i className="ri-building-2-line text-gray-400"></i>
                  </div>
                  <input
                    type="text"
                    value={subTenantId}
                    onChange={(e) => { setSubTenantId(e.target.value); setError(""); }}
                    placeholder="请输入租户编号"
                    autoComplete="organization"
                    className="w-full bg-white/10 border border-white/20 text-white placeholder-gray-500 rounded-2xl pl-11 pr-4 py-3.5 text-sm focus:outline-none focus:border-red-400 focus:bg-white/15 transition-all"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">账号</label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                    <i className="ri-user-line text-gray-400"></i>
                  </div>
                  <input
                    type="text"
                    value={account}
                    onChange={(e) => { setAccount(e.target.value); setError(""); }}
                    placeholder="请输入账号（手机号）"
                    autoComplete="username"
                    className="w-full bg-white/10 border border-white/20 text-white placeholder-gray-500 rounded-2xl pl-11 pr-4 py-3.5 text-sm focus:outline-none focus:border-red-400 focus:bg-white/15 transition-all"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">密码</label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                    <i className="ri-key-2-line text-gray-400"></i>
                  </div>
                  <input
                    type={showAccountPwd ? "text" : "password"}
                    value={accountPwd}
                    onChange={(e) => { setAccountPwd(e.target.value); setError(""); }}
                    placeholder="请输入登录密码"
                    autoComplete="current-password"
                    className="w-full bg-white/10 border border-white/20 text-white placeholder-gray-500 rounded-2xl pl-11 pr-12 py-3.5 text-sm focus:outline-none focus:border-red-400 focus:bg-white/15 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowAccountPwd(!showAccountPwd)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center cursor-pointer"
                  >
                    <i className={`${showAccountPwd ? "ri-eye-off-line" : "ri-eye-line"} text-gray-400 hover:text-gray-200 transition-colors`}></i>
                  </button>
                </div>
                {error && (
                  <div className="mt-2 flex items-center gap-2 text-red-400 text-xs">
                    <i className="ri-error-warning-line"></i>
                    {error}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || !subTenantId.trim() || !account.trim() || !accountPwd.trim()}
                className="w-full py-3.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-2xl text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                {loading ? (
                  <><i className="ri-loader-4-line animate-spin"></i>登录中...</>
                ) : (
                  <><i className="ri-login-box-line"></i>账号登录</>
                )}
              </button>

              <div className="flex items-start gap-3 bg-white/5 rounded-2xl p-3">
                <i className="ri-information-line text-amber-400 text-base flex-shrink-0 mt-0.5"></i>
                <p className="text-xs text-gray-400 leading-relaxed">
                  子账号由超级管理员在「系统设置 → 用户权限」中创建并分配密码
                </p>
              </div>
            </form>
          )}
        </div>

        {/* Platform CTA */}
        <div className="mt-5 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 bg-amber-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-apps-2-line text-amber-400"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-semibold mb-0.5">还没有协会管理系统？</p>
              <p className="text-gray-400 text-xs leading-relaxed">
                FanHub 为各地球迷协会提供一站式数字化管理平台，快速开通专属后台。
              </p>
              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={() => {
                    setDemoError("");
                    setShowDemo(true);
                  }}
                  className="flex items-center gap-1.5 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-white text-xs rounded-xl transition-colors cursor-pointer whitespace-nowrap font-medium"
                >
                  <i className="ri-rocket-line"></i> 申请免费体验
                </button>
                <a
                  href="/platform/login"
                  className="flex items-center gap-1.5 px-4 py-2 bg-white/10 hover:bg-white/15 text-gray-300 text-xs rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-shield-star-line"></i> 平台运营端
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* 按需求移除「返回球迷端首页」入口 */}
      </div>

      {/* Demo Request Modal */}
      {showDemo && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6">
            {demoSent ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-check-line text-green-600 text-2xl"></i>
                </div>
                <h3 className="font-bold text-gray-800 mb-1">申请已提交！</h3>
                <p className="text-sm text-gray-500">平台运营团队将在 1-2 个工作日内与您联系。</p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                    <i className="ri-apps-2-line text-amber-600"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800">申请免费体验</h3>
                    <p className="text-xs text-gray-400">FanHub 球迷协会管理平台</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowDemo(false);
                      setDemoError("");
                    }}
                    className="ml-auto w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>

                {demoError ? (
                  <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-3">{demoError}</p>
                ) : null}

                <div className="space-y-3 mb-5">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">协会/组织名称 *</label>
                    <input
                      type="text"
                      value={demoForm.orgName}
                      onChange={(e) => setDemoForm((p) => ({ ...p, orgName: e.target.value }))}
                      placeholder="如：XX市球迷协会"
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">联系人姓名 *</label>
                    <input
                      type="text"
                      value={demoForm.contact}
                      onChange={(e) => setDemoForm((p) => ({ ...p, contact: e.target.value }))}
                      placeholder="您的姓名"
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">联系电话</label>
                    <input
                      type="tel"
                      value={demoForm.phone}
                      onChange={(e) => setDemoForm((p) => ({ ...p, phone: e.target.value }))}
                      placeholder="方便接听的手机号"
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400"
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowDemo(false);
                      setDemoError("");
                    }}
                    className="flex-1 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap"
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleDemoSubmit()}
                    disabled={!demoForm.orgName.trim() || !demoForm.contact.trim() || demoSubmitting}
                    className="flex-1 py-2.5 text-sm text-white bg-amber-500 rounded-xl hover:bg-amber-400 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {demoSubmitting ? "提交中…" : "提交申请"}
                  </button>
                </div>

                <p className="text-xs text-gray-400 text-center mt-3">申请将提交到平台运营端，运营团队将与您联系。</p>
              </>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          15% { transform: translateX(-8px); }
          30% { transform: translateX(8px); }
          45% { transform: translateX(-6px); }
          60% { transform: translateX(6px); }
          75% { transform: translateX(-3px); }
          90% { transform: translateX(3px); }
        }
      `}</style>
    </div>
  );
}
